import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { env } from "~/config/env";
import { Box } from "~/design-system/Box";
import { Button } from "~/design-system/Button";
import { Heading } from "~/design-system/Heading";
import { Stack } from "~/design-system/Stack";
import { Text } from "~/design-system/Text";
import { FullPageSpinner } from "~/screens/App/components/FullPageSpinner/FullPageSpinner";

/**
 * Compare two semantic version strings.
 * Returns:
 * -1 if v1 < v2
 *  0 if v1 == v2
 *  1 if v1 > v2
 */
const compareVersions = (v1: string, v2: string): number => {
  // Parse ints to handle suffixes like "-beta", "-rc1" e.g. "1.1.9-beta" -> 1, 1, 9
  const parts1 = v1.split(".").map((p) => parseInt(p, 10));
  const parts2 = v2.split(".").map((p) => parseInt(p, 10));

  for (let i = 0; i < Math.max(parts1.length, parts2.length); i++) {
    const p1 = parts1[i] || 0;
    const p2 = parts2[i] || 0;
    if (p1 > p2) return 1;
    if (p1 < p2) return -1;
  }
  return 0;
};

const sanitizeStoreUrl = (rawUrl: string): string => {
  // Some environments inject quoted values. Normalize before opening in native browser.
  return rawUrl.trim().replace(/^['"]|['"]$/g, "");
};

type AppVersionCheckerProps = {
  children: React.ReactNode;
};

const isUpdateRequired = ({
  build,
  minBuild,
  minVersion,
  version,
}: {
  build: string;
  minBuild: number;
  minVersion: string;
  version: string;
}): boolean => {
  const versionComparison = compareVersions(version, minVersion);

  // If version is strictly less than min version -> Update required
  if (versionComparison < 0) {
    return true;
  }

  // If versions are equal, check build number
  if (versionComparison === 0) {
    return parseInt(build, 10) < minBuild;
  }

  return false;
};

export const AppVersionChecker = (props: AppVersionCheckerProps) => {
  const { t } = useTranslation();
  const [isChecking, setIsChecking] = React.useState(
    Capacitor.isNativePlatform()
  );
  const [updateRequired, setUpdateRequired] = React.useState(false);
  const [currentVersion, setCurrentVersion] = React.useState("");
  const [currentBuild, setCurrentBuild] = React.useState("");

  React.useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const checkVersion = async ({
      showBlockingSpinner = true,
    }: {
      showBlockingSpinner?: boolean;
    } = {}) => {
      try {
        if (showBlockingSpinner) {
          setIsChecking(true);
        }

        const info = await App.getInfo();
        const version = info.version; // e.g. "1.0.2"
        const build = info.build; // e.g. "15"

        setCurrentVersion(version);
        setCurrentBuild(build);

        const isIos = Capacitor.getPlatform() === "ios";
        const minVersion = isIos
          ? env.VITE_MIN_SUPPORTED_NATIVE_VERSION_IOS
          : env.VITE_MIN_SUPPORTED_NATIVE_VERSION_ANDROID;
        const minBuild = isIos
          ? env.VITE_MIN_SUPPORTED_NATIVE_BUILD_IOS
          : env.VITE_MIN_SUPPORTED_NATIVE_BUILD_ANDROID;

        setUpdateRequired(
          isUpdateRequired({ build, minBuild, minVersion, version })
        );
      } catch (error) {
        console.error("Failed to check app version", error);
      } finally {
        if (showBlockingSpinner) {
          setIsChecking(false);
        }
      }
    };

    void checkVersion({ showBlockingSpinner: true });

    // Re-check on resume without blocking UI to avoid remounting the app tree.
    const listener = App.addListener("appStateChange", (state) => {
      if (state.isActive) {
        void checkVersion({ showBlockingSpinner: false });
      }
    });

    return () => {
      void listener.then((l) => l.remove());
    };
  }, []);

  const handleUpdateClick = async () => {
    const rawStoreUrl =
      Capacitor.getPlatform() === "ios"
        ? env.VITE_IOS_APP_STORE_URL
        : env.VITE_ANDROID_APP_STORE_URL;
    const storeUrl = sanitizeStoreUrl(rawStoreUrl);

    if (!storeUrl) return;

    try {
      await Browser.open({ url: storeUrl });
    } catch {
      // Fallback for older native versions where Browser plugin might not be present
      window.location.href = storeUrl;
    }
  };

  if (isChecking) {
    return <FullPageSpinner />;
  }

  if (!updateRequired) {
    return <>{props.children}</>;
  }

  return (
    <Box
      css={{
        alignItems: "center",
        backgroundColor: "$neutral100",
        display: "flex",
        height: "100vh",
        justifyContent: "center",
        left: 0,
        padding: "$xlarge",
        position: "fixed",
        top: 0,
        width: "100vw",
        zIndex: 9990,
      }}
    >
      <Box css={{ maxWidth: "$sm", width: "100%" }}>
        <Stack space="xlarge" align="center">
          <Heading level="3">
            {t("app_version_checker.title", "Update Available")}
          </Heading>

          <Text size="standard" weight="medium" tone="secondary" align="center">
            {t(
              "app_version_checker.new_version_available_description",
              "A new version of Clovis is required to continue using the app."
            )}
          </Text>

          <Text size="small" tone="neutral" align="center">
            {t("app_version_checker.current_version", "Current Version")}:{" "}
            {currentVersion} ({currentBuild}) <br />
            {t(
              "app_version_checker.required_version",
              "Required Version"
            )}:{" "}
            {Capacitor.getPlatform() === "ios"
              ? env.VITE_MIN_SUPPORTED_NATIVE_VERSION_IOS
              : env.VITE_MIN_SUPPORTED_NATIVE_VERSION_ANDROID}{" "}
            (
            {Capacitor.getPlatform() === "ios"
              ? env.VITE_MIN_SUPPORTED_NATIVE_BUILD_IOS
              : env.VITE_MIN_SUPPORTED_NATIVE_BUILD_ANDROID}
            )
          </Text>

          <Button
            onClick={handleUpdateClick}
            variant="solid"
            tone="brandAccent"
            width="full"
            dataClovisActionId="app-version-checker-update"
          >
            {t("app_version_checker.update_app_button", "Update App")}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};
